// eslint-disable-next-line import/no-cycle
import type { modifierKeys } from "@modules/event_processor/types";
import type { Fields } from "@vuex-orm/core";
import platformInfo from "platform";
import { BrowserAction } from "./ActionHierarchy";
import type { Timings } from "./ActionTypes";

export class Keypress extends BrowserAction {
  // This is the name used as module name of the Vuex Store.
  public static entity = "keypress";

  public static baseEntity = "actions";

  public static fields(): Fields {
    return {
      ...super.fields(),
      key: this.string(""),
      modifierKeys: this.attr([]),
    };
  }

  public declare key: string;

  public declare modifierKeys: modifierKeys[];

  public get defaultTimings(): Pick<Timings, "before" | "main" | "after"> {
    if (this.speed === "fast")
      return {
        before: 0.1,
        main: 0.25,
        after: 0.1,
      };

    if (this.speed === "medium")
      return {
        before: 0.1,
        main: 0.3,
        after: 0.4,
      };

    return {
      before: 0.35,
      main: 1,
      after: 1,
    };
  }

  public static get humanName(): string {
    return "Press";
  }

  public get description(): string {
    const allKeys = (this.modifierKeys as string[]).concat([this.key]);
    const names = allKeys.map((modifierKey) => Keypress.describeKeyForVoiceOver(modifierKey));

    const sentence = `press ${names.join("-")}`;
    const formatted = sentence.charAt(0).toUpperCase() + sentence.slice(1);
    return formatted;
  }

  public static humanizeKey(
    key: string,
    platform?: "OS X" | "Windows",
  ): { symbol?: string; name: string } {
    switch (key) {
      case "Ctrl":
        if (platform === "OS X") {
          return { symbol: "⌃", name: "Control" };
        }
        return { name: "Ctrl" };
      case "Alt":
        if (platform === "OS X") {
          return { symbol: "⌥", name: "Option" };
        }
        return { name: "Alt" };
      case "Meta":
        if (platform === "Windows") {
          return { symbol: "⊞", name: "Windows" };
        }
        return { symbol: "⌘", name: "Command" };
      case "Shift":
        return { symbol: "⇧", name: "Shift" };
      default: {
        return { name: key };
      }
    }
  }

  public static describeKeyForVoiceOver(key: string): string {
    // We sniff the platform here because the voice over should match the platform of the person
    // making recording. Though we might want to revisit that later.
    const platform = platformInfo.os?.family;
    switch (key) {
      case "Ctrl":
        return "Control";
      case "Alt":
        if (platform === "OS X") {
          return "Option";
        }
        return "Alt";
      case "Meta":
        if (platform === "Windows") {
          return "Windows";
        }
        return "Command";
      default: {
        return key;
      }
    }
  }
}
